import { createPath } from "@/service/routing/routing";
import { cn } from "@/lib/utils";
import { getPriceForRefund, getPriceForRefundOther } from "../service";
import { FileText } from "lucide-react";

interface MedSubgroupsListProps {
  subgroups: MedGroupPreviewVM["versionSubgroups"];
  groupLink: string;
  superGroup: MedGroupPreviewVM["superGroup"];
  headless: boolean;
  trStyle: (isThead: boolean) => string;
  withoutRefunds?: boolean;
}

export function MedSubgroupsList({
  subgroups,
  groupLink,
  superGroup,
  headless,
  trStyle,
  withoutRefunds = false,
}: MedSubgroupsListProps) {
  return (
    <>
      {subgroups.map(({ versions, medVersionKind }, index) => {
        const versionGroupLink = createPath("meds.group.show", {
          slug: versions[0].groupSlug,
          id: versions[0].groupId,
        });
        const versionIsFromActiveGroup =
          superGroup && headless && groupLink === versionGroupLink;
        return (
          <div
            className={cn(
              "border-b first:mt-0 last:mb-0 last:border-b-0",
              versionIsFromActiveGroup && "bg-[#FAFAFA]",
            )}
            key={`${medVersionKind}-${index}`}
          >
            {versions.map((variant, subindex) => {
              const priceForRefundFull = getPriceForRefund(
                variant.prices,
                "100%",
                versionGroupLink,
              );
              const priceForRefundFifty = getPriceForRefund(
                variant.prices,
                "50%",
                versionGroupLink,
              );
              const priceForRefundThirty = getPriceForRefund(
                variant.prices,
                "30%",
                versionGroupLink,
              );
              const priceForRefundR = getPriceForRefund(
                variant.prices,
                "R",
                versionGroupLink,
              );

              const tdStyles = (centered: boolean, refund: boolean) =>
                cn(
                  `inline-flex items-start text-black text-xs font-normal`,
                  centered ? "justify-center" : "w-[80%]",
                  variant.disabled && "opacity-25",
                  refund &&
                    "px-2.5 bg-remedium-blue/10 w-fit rounded-sm items-center text-remedium-blue mx-auto",
                );

              const versionTitleStyle = "font-semibold text-black";

              const chplLink = variant.chplLink && (
                <div className="inline-flex" data-title={"ChPL"}>
                  <a
                    href={createPath("meds.chpl", { link: variant.chplLink })}
                    target={"_blank"}
                    className={"hover:text-remedium-blue transition-all"}
                  >
                    <FileText size={16} />
                  </a>
                </div>
              );

              return (
                <div
                  className={trStyle(false)}
                  key={`${subindex}-${variant.name}`}
                >
                  <div className={tdStyles(false, false)}>
                    <div className="mr-2">{chplLink}</div>

                    {versionIsFromActiveGroup || (!superGroup && headless) ? (
                      <div
                        className={cn(
                          versionTitleStyle,
                          versionIsFromActiveGroup && "text-teal-600",
                        )}
                      >
                        {variant.name}
                      </div>
                    ) : (
                      <a
                        href={versionGroupLink}
                        className={cn(
                          versionTitleStyle,
                          "hover:text-remedium-blue underline decoration-solid transition-all [text-decoration-skip-ink:none]",
                        )}
                        target={"_blank"}
                        rel="noreferrer"
                      >
                        {variant.name}
                      </a>
                    )}
                  </div>
                  <div className={tdStyles(false, false)}>
                    {variant.kind || "-"}
                  </div>
                  <div className={tdStyles(false, false)}>
                    {variant.dose || "-"}
                  </div>
                  <div className={tdStyles(false, false)}>
                    {variant.box || "-"}
                  </div>

                  <div className={tdStyles(true, priceForRefundFull !== "-")}>
                    {priceForRefundFull}
                  </div>
                  {!withoutRefunds && (
                    <>
                      <div
                        className={tdStyles(true, priceForRefundFifty !== "-")}
                      >
                        {priceForRefundFifty}
                      </div>
                      <div
                        className={tdStyles(true, priceForRefundThirty !== "-")}
                      >
                        {priceForRefundThirty}
                      </div>
                      <div className={tdStyles(true, priceForRefundR !== "-")}>
                        {priceForRefundR}
                      </div>
                      <div
                        className={cn(
                          tdStyles(true, false),
                          "flex gap-2 self-start",
                        )}
                      >
                        {getPriceForRefundOther(
                          variant.prices,
                          "B",
                          versionGroupLink,
                        )}
                        {getPriceForRefundOther(
                          variant.prices,
                          "S",
                          versionGroupLink,
                        )}
                        {getPriceForRefundOther(
                          variant.prices,
                          "DZ",
                          versionGroupLink,
                        )}
                        {getPriceForRefundOther(
                          variant.prices,
                          "C",
                          versionGroupLink,
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
    </>
  );
}
